import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import QRCode from "qrcode";
import { CONSENT_TEXT_VERSION, consentText, getLanguage, type LiveTurn, type ServerMessage, type SessionSummary, type TopicItem, type TurnLatency } from "@gk/shared";
import { api, t } from "../lib/api";
import { getToken } from "../lib/auth";
import { LiveClient, type ConnStatus } from "../lib/live";
import { fmtClock } from "../lib/hooks";
import { useSession, langName } from "../lib/session";

/**
 * The interviewer's phone. Built to keep eyes on the person, not the screen:
 * one big control, the other person's words in your language, topics to
 * cover rather than a script, and a quiet hint when an answer is worth a
 * follow-up.
 */
export function FieldInterviewerPage() {
  const { tenant, interviewId } = useParams();
  const { config } = useSession();
  const client = useRef<LiveClient | null>(null);
  const [session, setSession] = useState<SessionSummary | null>(null);
  const [state, setState] = useState("pairing");
  const [conn, setConn] = useState<ConnStatus>("connecting");
  const [peer, setPeer] = useState(false);
  const [turns, setTurns] = useState<LiveTurn[]>([]);
  const [caption, setCaption] = useState("");
  const [partial, setPartial] = useState<string | null>(null);
  const [topics, setTopics] = useState<TopicItem[]>([]);
  const [covered, setCovered] = useState<Set<string>>(new Set());
  const [suggestion, setSuggestion] = useState<{ kind: string; text: string } | null>(null);
  const [latency, setLatency] = useState<TurnLatency | null>(null);
  const [limit, setLimit] = useState<{ remainingSeconds: number; reached: boolean } | null>(null);
  const [qr, setQr] = useState<{ img: string; url: string } | null>(null);
  const [audioOn, setAudioOn] = useState(false);
  // Remembered on this phone: an interviewer who wears an earphone keeps hearing answers.
  const [speakAnswers, setSpeakAnswers] = useState(() => {
    try {
      return localStorage.getItem("kv.hearAnswers") === "1";
    } catch {
      return false;
    }
  });
  const [error, setError] = useState<string | null>(null);
  const [origin, setOrigin] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [showTopics, setShowTopics] = useState(false);
  const convoEnd = useRef<HTMLDivElement>(null);
  const speakAnswersRef = useRef(speakAnswers);
  const toggleHear = (on: boolean) => {
    setSpeakAnswers(on);
    speakAnswersRef.current = on;
    try {
      localStorage.setItem("kv.hearAnswers", on ? "1" : "0");
    } catch {
      /* private mode: remembered for this interview only */
    }
    client.current?.send({ t: "speak-answers", enabled: on });
  };
  const lastFinalAt = useRef(0);

  useEffect(() => {
    let c: LiveClient | null = null;
    void (async () => {
      const token = await getToken();
      if (!token) return;
      c = new LiveClient({
        role: "interviewer",
        interviewKey: `${tenant}:${interviewId}`,
        hello: { role: "interviewer", token, tenant, interviewId },
        onStatus: setConn,
        onMessage: (m: ServerMessage) => {
          switch (m.t) {
            case "welcome":
              // A new or restored connection: the server starts from "don't speak answers".
              c?.send({ t: "speak-answers", enabled: speakAnswersRef.current });
              setSession(m.session);
              setState(m.session.state);
              setOrigin(m.session.clockOrigin);
              setTurns(m.turns);
              setTopics(m.topics);
              setCovered(new Set(m.coveredQuestionIds));
              setPeer(m.peers.interviewee);
              if (m.session.limitSeconds != null) setLimit({ remainingSeconds: m.session.limitSeconds, reached: m.session.limitSeconds <= 0 });
              break;
            case "state":
              setState(m.state);
              if (m.clockOrigin) setOrigin(m.clockOrigin);
              if (m.consent && session) setSession((s) => (s ? { ...s, consent: m.consent ?? s.consent } : s));
              if (m.intervieweeLang) setSession((s) => (s ? { ...s, intervieweeLang: m.intervieweeLang! } : s));
              break;
            case "peer":
              if (m.role === "interviewee") setPeer(m.connected);
              break;
            case "caption":
              setCaption(m.text);
              break;
            case "turn":
              setTurns((prev) => {
                const i = prev.findIndex((x) => x.id === m.turn.id);
                const next = i >= 0 ? prev.map((x) => (x.id === m.turn.id ? m.turn : x)) : [...prev, m.turn];
                return next.sort((a, b) => a.seq - b.seq);
              });
              if (m.turn.speaker === "interviewer") setCaption("");
              else {
                setPartial(null);
                lastFinalAt.current = Date.now();
              }
              break;
            case "translation":
              if (!m.final && Date.now() - lastFinalAt.current > 1500) setPartial(m.text);
              break;
            case "covered":
              setCovered((prev) => {
                const n = new Set(prev);
                if (m.covered) n.add(m.questionId);
                else n.delete(m.questionId);
                return n;
              });
              break;
            case "suggestion":
              setSuggestion({ kind: m.kind, text: m.text });
              break;
            case "latency":
              setLatency(m.latency);
              break;
            case "limit":
              setLimit({ remainingSeconds: m.remainingSeconds, reached: m.reached });
              break;
            case "error":
              setError(m.message);
              break;
          }
        },
      });
      client.current = c;
    })();
    return () => c?.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, interviewId]);

  // A fresh pairing code whenever the participant's phone is not connected yet.
  // One phone recording the room: no second phone, consent is taken on this phone.
  const recordMode = session?.mode === "record";
  const needPairing = !recordMode && !peer && ["pairing", "consent"].includes(state);
  useEffect(() => {
    if (!needPairing || qr) return;
    void (async () => {
      try {
        const { url } = await api.post<{ url: string }>(`${t(tenant!)}/interviews/${interviewId}/pair`);
        setQr({ url, img: await QRCode.toDataURL(url, { margin: 1, width: 280, color: { dark: "#143628", light: "#ffffff" } }) });
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, [needPairing, qr, tenant, interviewId]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    convoEnd.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns.length, partial, caption]);

  const start = async () => {
    setError(null);
    try {
      await client.current!.enableAudio();
      setAudioOn(true);
      client.current!.send({ t: state === "paused" ? "resume" : "start" });
    } catch {
      setError("The microphone is blocked. Allow it for this site in your browser settings, then try again.");
    }
  };

  const elapsed = origin && ["live", "paused"].includes(state) ? now - origin : 0;
  const uncovered = topics.filter((x) => !covered.has(x.questionId));
  const topicGroups = useMemo(() => {
    const g = new Map<string, TopicItem[]>();
    for (const x of topics) g.set(x.topic, [...(g.get(x.topic) ?? []), x]);
    return [...g.entries()];
  }, [topics]);

  const statusPill =
    state === "live" ? (
      <span className="pill ochre live">
        <span className="dot" />
        Live {fmtClock(elapsed)}
      </span>
    ) : state === "paused" ? (
      <span className="pill amber">Paused {fmtClock(elapsed)}</span>
    ) : state === "ended" ? (
      <span className="pill">Ended</span>
    ) : state === "ready" ? (
      <span className="pill green">Consent given</span>
    ) : recordMode ? (
      <span className="pill">Consent needed</span>
    ) : (
      <span className="pill">{peer ? "Waiting for consent" : "Waiting for the second phone"}</span>
    );

  const recordConsent = (accepted: boolean) => {
    if (!session) return;
    client.current?.send({ t: "consent", accepted, lang: session.intervieweeLang, textVersion: CONSENT_TEXT_VERSION });
  };

  return (
    <div className="field-app">
      <header className="field-top">
        <Link to={`/w/${tenant}/i/${interviewId}`} className="btn ghost small" aria-label="Back to the interview">
          Back
        </Link>
        <div>
          <div className="who">{session?.engagementName ?? "Interview"}</div>
          <div className="muted xs">
            {session
              ? recordMode
                ? `Recording on this phone · ${langName(config, session.intervieweeLang)}${session.participants > 1 ? ` · group of ${session.participants}` : ""}`
                : `${langName(config, session.interviewerLang)} ↔ ${langName(config, session.intervieweeLang)}`
              : ""}
          </div>
        </div>
        <span className="spacer" />
        {conn !== "open" && <span className="pill amber">{conn === "reconnecting" ? "Reconnecting…" : conn === "closed" ? "Disconnected" : "Connecting…"}</span>}
        {!recordMode && latency?.totalMs != null && (
          <span className="pill lag" title="Time from the end of speech to the translated voice starting">
            Delay {(latency.totalMs / 1000).toFixed(1)} s{latency.prepared ? " · prepared" : ""}
          </span>
        )}
        {!recordMode && (
        <button
          type="button"
          className={`btn small ${speakAnswers ? "primary" : ""}`}
          aria-pressed={speakAnswers}
          onClick={() => toggleHear(!speakAnswers)}
          title="Hear the participant's answers in your language through an earphone"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
            <rect x="3" y="14" width="4" height="6" rx="1.5" />
            <rect x="17" y="14" width="4" height="6" rx="1.5" />
          </svg>
          {speakAnswers ? "Hearing answers" : "Hear answers"}
        </button>
        )}
        {statusPill}
      </header>

      {error && <div className="error-box" style={{ margin: 12 }}>{error}</div>}
      {limit && limit.remainingSeconds < 120 && (
        <div className="note-box" style={{ margin: 12 }}>
          {limit.reached ? "The free plan's 5 interview minutes are used up. The interview has been paused." : `${Math.ceil(limit.remainingSeconds / 60)} free-plan minute(s) left.`}
        </div>
      )}

      <div className="field-body">
        <main className="convo" aria-live="polite">
          {needPairing && (
            <div className="pairing">
              <h2>Hand the second phone to the participant</h2>
              <p className="muted" style={{ maxWidth: 420 }}>
                They scan this code with its camera. Their screen asks for consent in their language; nothing is recorded before they agree.
              </p>
              {qr ? (
                <img className="qr" src={qr.img} width={260} height={260} alt="Pairing code for the participant's phone" />
              ) : (
                <div className="qr" style={{ width: 260, height: 260 }} />
              )}
              <div className="row wrap" style={{ justifyContent: "center" }}>
                <button className="btn small" onClick={() => setQr(null)}>
                  New code
                </button>
                {qr && (
                  <button className="btn small ghost" onClick={() => void navigator.clipboard?.writeText(qr.url)}>
                    Copy link
                  </button>
                )}
              </div>
            </div>
          )}
          {recordMode && session && ["pairing", "consent"].includes(state) && (
            <div className="pairing" style={{ textAlign: "left", alignItems: "stretch" }}>
              <h2>Read this to {session.participants > 1 ? "everyone taking part" : "the participant"} before you start</h2>
              <div className="consent-read" lang={session.intervieweeLang}>
                {consentText(getLanguage(session.intervieweeLang).base, session.country, session.controller)}
              </div>
              <p className="muted small">Nothing is recorded until you confirm. If anyone does not agree, do not record this conversation.</p>
              <div className="row wrap">
                <button className="btn primary" onClick={() => recordConsent(true)}>
                  {session.participants > 1 ? "Everyone agreed" : "They agreed"}
                </button>
                <button className="btn ghost" onClick={() => recordConsent(false)}>
                  Someone said no
                </button>
              </div>
            </div>
          )}
          {!recordMode && !needPairing && state === "consent" && (
            <div className="pairing">
              <h2>The participant is reading the consent</h2>
              <p className="muted">Give them a moment. You can start as soon as they agree.</p>
            </div>
          )}
          {state === "ready" && turns.length === 0 && (
            <div className="pairing">
              <h2>Ready when you are</h2>
              <p className="muted" style={{ maxWidth: 440 }}>
                {recordMode
                  ? "Put the phone in the middle of the table, screen up, and talk normally. Everyone is recorded; who said what is worked out after the conversation."
                  : "Talk naturally and look at the person, not the phone. Pause briefly when you finish a sentence and the translation will follow."}
              </p>
            </div>
          )}

          {turns.map((x) =>
            x.speaker === "interviewer" ? (
              <div className="bubble me" key={x.id}>
                <div className="main-text">{x.sourceText}</div>
                {x.targetText && x.targetText !== x.sourceText && <div className="sub-text">{x.targetText}</div>}
                <div className="meta">
                  <span>{fmtClock(x.startedMs)}</span>
                  {x.preparedQuestionId && <span>Prepared question</span>}
                </div>
              </div>
            ) : (
              <div className="bubble them" key={x.id}>
                <div className="main-text">{x.targetText ?? x.sourceText}</div>
                {x.targetText && x.targetText !== x.sourceText && <div className="sub-text">{x.sourceText}</div>}
                <div className="meta">
                  <span>{fmtClock(x.startedMs)}</span>
                </div>
              </div>
            ),
          )}
          {partial && (
            <div className="bubble them live">
              <div className="main-text">{partial}</div>
            </div>
          )}
          {caption && (
            <div className="bubble me live">
              <div className="main-text">{caption}</div>
            </div>
          )}
          {state === "ended" && (
            <div className="pairing">
              <h2>Interview ended</h2>
              <p className="muted">The recording is being assembled and analysed. You can review it in a minute or two.</p>
              <Link className="btn primary" to={`/w/${tenant}/i/${interviewId}`}>
                Go to the review
              </Link>
            </div>
          )}
          <div ref={convoEnd} />
        </main>

        <aside className="side" style={{ display: showTopics || window.innerWidth > 900 ? undefined : "none" }}>
          {suggestion && (
            <div className="suggestion" role="status">
              <b>{suggestion.kind === "missing_number" ? "Ask for numbers" : suggestion.kind === "risk" ? "Worth probing" : "Follow-up"}</b>
              {suggestion.text}
              <div>
                <button className="btn ghost small" style={{ paddingLeft: 0 }} onClick={() => setSuggestion(null)}>
                  Dismiss
                </button>
              </div>
            </div>
          )}
          <div>
            <div className="row" style={{ marginBottom: 6 }}>
              <h4>Topics</h4>
              <span className="spacer" />
              <span className="muted xs">{topics.length - uncovered.length} of {topics.length} covered</span>
            </div>
            {topics.length === 0 && <p className="muted small">No question guide for this engagement yet.</p>}
            {topicGroups.map(([topic, qs]) => (
              <div key={topic}>
                {qs.map((q) => {
                  const done = covered.has(q.questionId);
                  return (
                    <label key={q.questionId} className={`topic ${done ? "done" : ""}`}>
                      <input type="checkbox" checked={done} onChange={(e) => client.current?.send({ t: "topic", questionId: q.questionId, covered: e.target.checked })} />
                      <span>
                        <span className="xs muted">{topic}</span>
                        <div className="t small">{q.text}</div>
                        {q.verified && !recordMode && <div className="xs faint">Verified translation — plays instantly</div>}
                      </span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>
      </div>

      <footer className="field-controls">
        {(state === "ready" || state === "paused") && (
          <button className="btn primary big" onClick={() => void start()} disabled={limit?.reached}>
            {state === "paused" ? "Resume" : recordMode ? "Start recording" : "Start interview"}
          </button>
        )}
        {state === "live" && !audioOn && (
          <button className="btn accent big" onClick={() => void start()}>
            Turn my microphone on
          </button>
        )}
        {state === "live" && (
          <button className="btn big" onClick={() => client.current?.send({ t: "pause" })}>
            Pause
          </button>
        )}
        <button className="btn ghost" onClick={() => setShowTopics((v) => !v)} style={{ display: window.innerWidth > 900 ? "none" : undefined }}>
          {showTopics ? "Hide topics" : `Topics (${uncovered.length} left)`}
        </button>
        <span className="spacer" />
        {["live", "paused", "ready"].includes(state) && (
          <button
            className="btn danger"
            onClick={() => {
              if (window.confirm("End the interview? Recording stops for both phones.")) client.current?.send({ t: "end" });
            }}
          >
            End interview
          </button>
        )}
      </footer>
    </div>
  );
}
